import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Tooltip } from 'bootstrap';
import { BreakpointDirective } from 'src/app/directives/breakpoint.directive';
import { CommonService } from 'src/app/services/common.service';
import { SearchService } from 'src/app/services/search.service';

import { TbtkSideNavService } from './sidenav.service';

export interface SideNav {
  header: string;
  items: SideNavItem[];
  quota: SideNavQuota;
}

export interface SideNavItem {
  label: string;
  icon?: string;
  link?: string;
  space?: boolean;
}

export interface SideNavQuota {
  label: string;
  percentage: number;
  usage: string;
  total: string;
}

/**
 * The sidenav component is designed to add sidenav content to a small section of your app. Left sidenav menu can be opened or closed using the openCloseSideNav() methods.
 *
 * ### Component Details
 *
 * The sidenav component is designed to add sidenav content to a small section of your app. Left sidenav menu can be opened or closed using the `openCloseSideNav()` methods.
 *
 * SideNav component accepts [items], [header], [quoata], [bottom] properties which are the content to be displayed in the sidenav.
 *
 * ```ts
 * @Input() header: string - Items to be displayed in the sidenav header.
 * @Input() items: Array<SideNavItem> - Items to be displayed in the sidenav body.
 * @Input() quota: SideNavQuota - Items to be displayed in the sidenav quota.
 * ```
 *
 * Add the below code to your code stack and give initializer parameters.
 *
 * <sub>app.component.html</sub>
 *
 * ```html
 * <tbtk-sidenav [items]="sidebar" [header]="'Safir Depo'" [quota]="sidebarQuota"></tbtk-sidenav>
 * ```
 *
 * Add the below code to your code stack to change the sidenav width and arrange place of active line (blue).
 *
 * <sub>app.component.scss</sub>
 *
 * ```scss
 * // Add the below code to your code stack if window size > 768px
 * .container tbtk-sidenav {
 *   --sidenav-width: 250px; // these two  with icon sidenav size
 *   --li-container-width: 233px;
 *   --sidenav-icon-left: 60px;
 *   --sidenav-line-left: 96px; // these three  only icon sidenav size
 *   --sidenav-only-icon-width: 100px;
 *   --active-line-color: #8ab2de;
 *   --quota-text-color: #316baa; // change color depends on the app
 *   --active-text-color: #0a4d95;
 *   --quota-icon-color: #357faa;
 * }
 * ```
 *
 * Below is the code used to see the sample @input properties of the Sidenav component.
 *
 * <sub>app.component.ts</sub>
 *
 * ```ts
 * const side: SideNav = {
 *   "header": "Safir Depo",
 *   "items": [
 *     { "label": "Ana Sayfa", "icon": "ti-home", "link": "/anasayfa" },
 *     { "label": "Depom", "icon": "ti-package", "link": "/depom" },
 *     { "label": "Şifreli Depom", "icon": "ti-lock-on", "link": "/sifrelidepom" },
 *     { "label": "Paylaşımlar", "icon": "ti-share-1", "link": "/paylasimlar", "space": true },
 *     { "label": "Favoriler", "icon": "ti-star", "link": "/favoriler" },
 *     { "label": "Yakın Tarihli", "icon": "ti-stopwatch", "link": "/yakintarihli" },
 *     { "label": "Etiketler", "icon": "ti-tag", "link": "/etiketler", "space": true },
 *     { "label": "Gruplarım", "icon": "ti-user", "link": "/gruplarim" },
 *     { "label": "Çöp Kutusu", "icon": "ti-trash", "link": "/copkutusu" }
 *   ],
 *   "quota": {
 *     "label": "Depolama Alanım",
 *     "percentage": 56,
 *     "usage": "132.92 KB ",
 *     "total": "10 GB"
 *   }
 * };
 * ```
 */
@Component({
  selector: 'tbtk-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: true,
  imports: [CommonModule, BreakpointDirective, RouterModule],
})
export class TbtkSideNavComponent implements AfterViewInit {
  @Input() public header = '';
  @Input() public items: Array<SideNavItem> = [];
  @Input() public quota: SideNavQuota | Record<string, any> = {};
  public viewState?: boolean = false;
  currentYear: number = new Date().getFullYear();

  constructor(public sideNavService: TbtkSideNavService, public commonService: CommonService, private elementRef: ElementRef, private searchService: SearchService) {}

  ngAfterViewInit(): void {
    const menuIcon = this.elementRef.nativeElement.querySelectorAll('[data-bs-toggle="sidenav-tooltip"]');
    menuIcon.forEach((item: HTMLSpanElement) => {
      item && new Tooltip(item, { container: document.body });
    });
  }

  checkRoute(item: SideNavItem): void {
    if (item.link === '/depom') {
      this.resetSearch();
    }
  }

  resetSearch(): void {
    this.searchService.setIsSearchDetail(false);
    this.searchService.setSearchText('');
    this.searchService.setResetSearchForm();
    this.commonService.rebindListener.next();
  }

  openCloseSideNav(): void {
    this.sideNavService.isClose = !this.sideNavService.isClose;
  }
}
